/**
 * useUserEmail Hook
 * 
 * React hook for managing user email state for deal tracking.
 * Automatically fetches/creates user email on mount when user is authenticated.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../components/auth/AuthWrapper';
import { getOrCreateUserEmail, getUserEmailStats } from '../services/userEmailApi';

/**
 * Hook to manage user's unique email address for deal tracking
 * 
 * @returns {Object} User email state and actions
 */
const useUserEmail = () => {
  const { user, isAuthenticated } = useAuth();
  const [userEmail, setUserEmail] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Extract user info from auth context
  const userId = user?.userId;
  const firstname = user?.attributes?.given_name || user?.username || 'user';

  /**
   * Fetch or create user email
   */
  const fetchUserEmail = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getOrCreateUserEmail(userId, firstname);
      setUserEmail(response.email);
      
      // Log if newly created
      if (response.is_new) {
        console.log(`📧 Created new tracking email: ${response.email.email_address}`);
      }
    } catch (err) {
      console.error('Failed to fetch user email:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, firstname]);

  /**
   * Fetch user email statistics
   */
  const fetchStats = useCallback(async () => {
    if (!userId) return;

    try {
      const statsData = await getUserEmailStats(userId);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch email stats:', err);
      // Don't set error for stats - it's optional
    }
  }, [userId]);

  /**
   * Refresh both email and stats
   */
  const refresh = useCallback(async () => {
    await fetchUserEmail();
    await fetchStats();
  }, [fetchUserEmail, fetchStats]);

  // Auto-fetch on mount when authenticated
  useEffect(() => {
    if (isAuthenticated && userId) {
      fetchUserEmail();
    }
  }, [isAuthenticated, userId, fetchUserEmail]);

  return {
    // State
    userEmail,
    emailAddress: userEmail?.email_address || null,
    emailId: userEmail?.email_id || null,
    isActive: userEmail?.is_active ?? true,
    stats,
    loading,
    error,
    
    // User info
    userId,
    
    // Actions
    refresh,
    fetchStats,
  };
};

export default useUserEmail;

